import { useNavigate } from "react-router-dom";
import { InputAdornment, IconButton } from "@mui/material";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { colors } from "../../../../styles";
import { DesktopHeader, MobileHeader } from "../Account/components";
import { employeeAccountPath } from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";
import { ReactComponent as DollarBillIcon } from "../../../../assets/icons/dollarbill.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/icons/copy.svg";
import {
  SectionFlexColumn,
  SectionFlexRow,
  TextInput,
} from "../../../../components/General";
import useGetAccount from "../../../../hooks/queries/employee/useGetAccount";
import { copyToClipboard } from "../../../../helpers/data";
import useGetReferrals from "../../../../hooks/queries/employee/useGetReferrals";
import { UserReferral } from "../../../../types";
import { LinkConstants } from "../../../../helpers/links";

const InviteCoworkers = () => {
  const { data: accountData } = useGetAccount();
  const { data: referralData, status } = useGetReferrals();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const title = "Invite Coworkers";
  const subtitle = "Help your coworkers grow their savings";
  const breadcrumbs = ["My Account", title];

  const doBack = () => {
    navigate(employeeAccountPath);
  };
  const standardMarginTop = "32px";

  const referralLink =
    accountData?.employer.coworker_referral_program.referral_url;

  const renderReferrals = () => {
    const getStatusText = (status: UserReferral["status"]) => {
      switch (status) {
        case "onboarding":
          return { text: "Onboarding", color: colors.black };
        case "completed":
          return { text: "Completed", color: colors.dark_green };
        default:
          return { text: "Onboarding", color: colors.black };
      }
    };
    if (referralData) {
      return referralData.map((referral) => {
        const { text, color } = getStatusText(referral.status);
        return (
          <SectionFlexRow sx={{}}>
            <T variant="mdReg" sx={{}}>
              {referral.referred_name} -
            </T>
            <T variant="mdReg" sx={{ color: color, marginLeft: "4px" }}>
              {text}
            </T>
          </SectionFlexRow>
        );
      });
    }
    return <T variant="mdReg">No referrals yet</T>;
  };
  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        <SectionFlexColumn
          sx={{ alignItems: "center", marginTop: standardMarginTop }}
        >
          <DollarBillIcon />
          <T variant="mdReg" marginTop={standardMarginTop} textAlign="center">
            Earn $5 for every coworker you refer to TIFIN @Work! Your coworker
            will get a $5 sign up bonus when they open an account. Referred
            coworkers must sign up for Sponsored Savings to qualify
          </T>
          {referralLink && (
            <TextInput
              size="small"
              value={referralLink}
              sx={{ marginTop: standardMarginTop, color: colors.gray_600 }}
              disabled
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="copy referral link"
                      onClick={() => copyToClipboard(referralLink)}
                      size={"small"}
                      edge="end"
                    >
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <SectionFlexColumn
            sx={{ width: "100%", marginTop: standardMarginTop }}
          >
            <T variant="lgMed">Recent Activity</T>
            <div className="mt-16">
              {status === "loading" && <div>Loading...</div>}
              {status === "error" && <div>Error</div>}
              {renderReferrals()}
            </div>
            <div className="mt-32">
              <T
                variant="mdReg"
                sx={{
                  textDecoration: "underline",
                  color: colors.gray_600,
                  cursor: "pointer",
                }}
                onClick={() =>
                  window.open(LinkConstants.referralTermOfServices, "_blank")
                }
              >
                Terms and Conditions
              </T>
            </div>
          </SectionFlexColumn>
        </SectionFlexColumn>
      </ResponsePageContent>
    </Page>
  );
};

export default InviteCoworkers;
